import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import Seo from '@/components/Seo';
import { PUBLIC_SEO, DEFAULT_PUBLIC_SEO } from '@/lib/publicSeo';

const navLinks = [
  { href: '/for-professionals', label: 'For Professionals' },
  { href: '/for-appraisers', label: 'For Appraisers' },
  { href: '/how-it-works', label: 'How It Works' },
  { href: '/pricing', label: 'Pricing' },
];

const LOGO_URL = '/logo-white.png';
const LOGO_DARK_URL = '/logo-blue.png';

export default function PublicLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Ensure public pages are never affected by dark mode
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.style.backgroundColor = '';
  }, [location.pathname]);

  useEffect(() => {
    // Only handle hash scrolling on the homepage
    if (location.pathname === '/' && location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
        return;
      }
    }
    // Always scroll to top on route change (not hash change)
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'appraiser') return '/appraiser';
    return '/professional';
  };

  const dashboardLabel = (
    user?.role === 'professional' && user?.subscription_status === 'pending'
      ? 'Complete Signup'
      : 'Dashboard'
  );

  const isActive = (href) => {
    if (href.startsWith('/#')) return false;
    return location.pathname === href;
  };

  const seo = PUBLIC_SEO[location.pathname] || DEFAULT_PUBLIC_SEO;

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="public-layout" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Seo
        title={seo.title}
        description={seo.description}
        path={location.pathname}
        image={seo.image}
        noIndex={seo.noIndex}
      />
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-[72px]">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src={LOGO_DARK_URL} alt="Appraisal Delivery" className="h-10 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 transition-colors text-[#0E264C] hover:text-[#147A72] ${
                    isActive(link.href) ? 'nav-link-active' : ''
                  }`}
                  style={{ fontSize: '14px', fontWeight: 500 }}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <Link
                  to={getDashboardLink()}
                  data-testid="dashboard-btn"
                  className="px-6 py-2.5 rounded-full bg-[#0E264C] text-white hover:bg-[#162e52] transition-all"
                  style={{ fontSize: '14px', fontWeight: 600 }}
                >
                  {dashboardLabel}
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="login-btn"
                    className="px-6 py-2.5 rounded-full border border-[#E2E8F0] text-[#1B2B4B] hover:bg-[#F8FAFC] transition-all"
                    style={{ fontSize: '14px', fontWeight: 500 }}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    data-testid="get-started-btn"
                    className="px-6 py-2.5 rounded-full bg-[#0E264C] text-white hover:bg-[#162e52] transition-all"
                    style={{ fontSize: '14px', fontWeight: 700 }}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 text-gray-600"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white px-4 py-4 space-y-2">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-gray-100 flex flex-col gap-2">
              {user ? (
                <Link to={getDashboardLink()} onClick={() => setMobileOpen(false)}>
                  <div className="w-full text-center py-2.5 rounded-full bg-[#0E264C] text-white text-sm font-semibold">{dashboardLabel}</div>
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)}>
                    <div className="w-full text-center py-2.5 rounded-full border border-[#E2E8F0] text-[#1B2B4B] text-sm font-medium">Sign In</div>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileOpen(false)}>
                    <div className="w-full text-center py-2.5 rounded-full bg-[#0E264C] text-white text-sm font-bold">Get Started</div>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#0F1729] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <img src={LOGO_URL} alt="Appraisal Delivery" className="h-10 w-auto mb-5" />
              <p className="text-white/55 max-w-sm" style={{ fontSize: '14px', lineHeight: 1.65 }}>
                A professional technology platform connecting professionals with independent real estate appraisers.
              </p>
            </div>
            <div className="md:col-span-3">
              <h4 className="text-white/80 mb-4" style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Platform</h4>
              <div className="space-y-3">
                <Link to="/for-professionals" className="block text-white/55 hover:text-white transition-colors" style={{ fontSize: '14px' }}>For Professionals</Link>
                <Link to="/for-appraisers" className="block text-white/55 hover:text-white transition-colors" style={{ fontSize: '14px' }}>For Appraisers</Link>
                <Link to="/how-it-works" className="block text-white/55 hover:text-white transition-colors" style={{ fontSize: '14px' }}>How It Works</Link>
                <Link to="/pricing" className="block text-white/55 hover:text-white transition-colors" style={{ fontSize: '14px' }}>Pricing</Link>
              </div>
            </div>
            <div className="md:col-span-3">
              <h4 className="text-white/80 mb-4" style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Company</h4>
              <div className="space-y-3">
                <Link to="/about" className="block text-white/55 hover:text-white transition-colors" style={{ fontSize: '14px' }}>About</Link>
                <Link to="/terms" className="block text-white/55 hover:text-white transition-colors" style={{ fontSize: '14px' }}>Terms of Service</Link>
                <Link to="/privacy" className="block text-white/55 hover:text-white transition-colors" style={{ fontSize: '14px' }}>Privacy Policy</Link>
                <Link to="/contact" className="block text-white/55 hover:text-white transition-colors" style={{ fontSize: '14px' }}>Contact</Link>
              </div>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-white/55" style={{ fontSize: '13px' }}>&copy; 2026 Appraisal Delivery Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
